import React, { useCallback, useEffect, useState, useRef } from 'react';
import { SummarizePayload } from '../../hooks/useSummary';
import OptionSelector from '../Home/OptionSelector';
import { config } from '../../config';

const STORAGE_KEY = 'summary_settings'; // Updated priority order

type Props = {
  isLoading: boolean;
  summarizePayload: SummarizePayload | undefined;
  availableStages: Array<{ id: string; name: string; isDefault: boolean }>;
  availableModels: string[];
  savedSummaryConfig: SummarizePayload | null;
  setSummarizePayload: (summarizePayload: SummarizePayload) => void;
};

export const SummaryToolbar = ({
  isLoading,
  summarizePayload,
  setSummarizePayload,
  availableStages,
  availableModels,
  savedSummaryConfig,
}: Props) => {
  const [selectedModel, setSelectedModel] = useState<string>(config.summaryDefaultModel);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [openModel, setOpenModel] = useState<boolean>(false);
  const [openPrompt, setOpenPrompt] = useState<boolean>(false);
  const initializedRef = useRef<string | null>(null);

  const saveSettingsToStorage = useCallback((payload: SummarizePayload) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, []);

  const loadSettingsFromStorage = useCallback(() => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings) as SummarizePayload;
        setSelectedModel(parsedSettings.model);
        setSelectedPromptId(parsedSettings.promptId);
        setSummarizePayload(parsedSettings);
        return true;
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
    return false;
  }, [setSummarizePayload]);

  const handleModelChange = useCallback(
    (model: string) => {
      setSelectedModel(model);
      const newPayload = {
        model,
        promptId: selectedPromptId || availableStages[0]?.id || '',
      };
      setSummarizePayload(newPayload);
      saveSettingsToStorage(newPayload);
    },
    [selectedPromptId, availableStages, setSummarizePayload, saveSettingsToStorage]
  );

  const handlePromptChange = useCallback(
    (stage: { id: string; name: string; isDefault: boolean }) => {
      setSelectedPromptId(stage.id);
      const newPayload = {
        model: selectedModel,
        promptId: stage.id,
      };
      setSummarizePayload(newPayload);
      saveSettingsToStorage(newPayload);
    },
    [selectedModel, setSummarizePayload, saveSettingsToStorage]
  );

  useEffect(() => {
    // Only initialize when we have the necessary data
    if (availableStages.length === 0 || availableModels.length === 0) return;

    // Check if we've already initialized with the current savedSummaryConfig
    const currentConfigKey = savedSummaryConfig
      ? `${savedSummaryConfig.model}-${savedSummaryConfig.promptId}`
      : 'null';
    if (initializedRef.current === currentConfigKey) return;

    // Priority order: 1) Saved summary config, 2) localStorage, 3) API is_default
    let settingsLoaded = false;

    // 1. First priority: Use saved summary config if available
    if (savedSummaryConfig) {
      const isValidModel = availableModels.includes(savedSummaryConfig.model);
      const isValidPrompt = availableStages.some((stage) => stage.id === savedSummaryConfig.promptId);

      if (isValidModel && isValidPrompt) {
        setSelectedModel(savedSummaryConfig.model);
        setSelectedPromptId(savedSummaryConfig.promptId);
        setSummarizePayload(savedSummaryConfig);
        settingsLoaded = true;
      }
    }

    // 2. Second priority: Load from localStorage if no saved summary config
    if (!settingsLoaded) {
      settingsLoaded = loadSettingsFromStorage();
    }

    // 3. Third priority: Use API is_default if no saved settings
    if (!settingsLoaded) {
      const configModel = availableModels.includes(config.summaryDefaultModel)
        ? config.summaryDefaultModel
        : availableModels[0] || config.summaryDefaultModel;
      const defaultPromptId =
        availableStages.find((stage) => stage.isDefault)?.id || availableStages[0]?.id || '';

      setSelectedModel(configModel);
      setSelectedPromptId(defaultPromptId);

      const defaultPayload = {
        model: configModel,
        promptId: defaultPromptId,
      };
      setSummarizePayload(defaultPayload);
      saveSettingsToStorage(defaultPayload);
    }

    // Mark as initialized with current config
    initializedRef.current = currentConfigKey;
  }, [availableStages, availableModels, savedSummaryConfig]);

  if (isLoading) return <div>Nalagam konfiguracijo...</div>;

  // Find the current selected stage for the prompt selector
  const selectedStage = availableStages.find((stage) => stage.id === selectedPromptId);

  return (
    <div className="summary_modal_toolbar">
      <div className="summary_modal_settings">
        <OptionSelector
          opened={openModel}
          title="Model"
          value={selectedModel}
          options={availableModels}
          updateValue={handleModelChange}
          handleOpened={setOpenModel}
          disablePortal={true}
          extractValues={(value) =>
            value === 'gpt-4o-mini' ? 'Standard' : value === 'gpt-5' ? 'Pro' : value
          }
        />

        {availableStages.length > 0 && (
          <OptionSelector
            opened={openPrompt}
            title="Predloga"
            value={selectedStage}
            options={availableStages}
            updateValue={handlePromptChange}
            handleOpened={setOpenPrompt}
            disablePortal={true}
            extractValues={(stage) => stage.name}
          />
        )}
      </div>
    </div>
  );
};
